import { useState } from 'react'
import { Drawer, DrawerSection } from '@/shared/ui/Drawer'
import { ErpTable, Th, Td, EmptyRow } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import {
  coordinatorContact, enrolMember, getCell, identifyByNationalId, listCellMembers,
  recordMemberConsent, removeMember, setCoordinator, unsetCoordinator,
  type TurnoutIdentity,
} from './api'
import { MemberConsentPanel } from './MemberConsentPanel'
import { label } from './vocab'

/**
 * One cell: who is in it, and adding somebody by the ID card in front of you.
 *
 * <p>Cells are built by hand. The assignment engine is an accelerator for a station with
 * a full register loaded; it is not the only way in, and for a mobilizer walking a street
 * it is not the usual way. Everything here has existed on the server since the feature
 * shipped — {@code POST /cells}, {@code POST /cells/&#123;id&#125;/members},
 * {@code DELETE …/members/&#123;id&#125;}, {@code POST …/coordinator} — and no screen
 * called any of it.</p>
 */
export function CellDrawer({
  cellId, onClose, onChanged,
}: { cellId: string | null; onClose: () => void; onChanged: () => void }) {
  const canEnrol = useCan('turnout:enrol')
  const canManage = useCan('turnout:manage-cells')
  // Seeing that a cell HAS a coordinator is turnout:read. The number itself is
  // turnout:read-raw — §14.1's authority for turning this module's masks into values.
  const canCallOut = useCan('turnout:read-raw')

  const cell = useLoad(() => (cellId ? getCell(cellId) : Promise.resolve(null)), [cellId])
  const members = useLoad(
    () => (cellId ? listCellMembers(cellId) : Promise.resolve([])), [cellId])

  const [nationalId, setNationalId] = useState('')
  const [identity, setIdentity] = useState<TurnoutIdentity | null>(null)
  const [consent, setConsent] = useState(false)
  // Asked at the same moment and recorded separately: appearing on a board and being
  // telephoned are different agreements, and the register keeps them apart.
  const [callConsent, setCallConsent] = useState(false)
  const [openConsentFor, setOpenConsentFor] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!cellId) return null

  const reset = () => {
    setNationalId(''); setIdentity(null); setConsent(false); setCallConsent(false)
  }

  /**
   * Ask for the coordinator's number and hand it to the OS.
   *
   * Not a `tel:` href rendered ahead of time. The old Call button on the board was
   * `href={tel:${member.voterId}}` — it dialled a UUID, because no phone number has ever
   * been on that response. The server checks suppression and consent and audits the
   * reveal, so the number can only be asked for at the moment somebody means to dial.
   */
  const call = async () => {
    if (!cellId) return
    setBusy(true)
    setError(null)
    try {
      const contact = await coordinatorContact(cellId)
      if (!contact.contactable) {
        setError(whyNotCallable(contact.displayName, contact.reasonCode))
        return
      }
      if (!contact.phoneE164) {
        setError(`The register holds no phone number for ${contact.displayName}.`)
        return
      }
      window.location.href = `tel:${contact.phoneE164}`
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await work()
      members.reload()
      cell.reload()
      onChanged()
    } catch (e) {
      // 409 turnout/member-already-enrolled says which cell they are in. That sentence is
      // the whole answer, so it is shown rather than replaced with "could not add".
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const identify = async () => {
    setBusy(true)
    setError(null)
    setIdentity(null)
    try {
      setIdentity(await identifyByNationalId(nationalId.trim()))
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const rows = members.data ?? []

  return (
    <Drawer open onClose={onClose} title={cell.data?.code ?? 'Cell'}
      subtitle={cell.data?.label ?? undefined}>
      <ErrorStrip message={error ?? cell.error ?? members.error} />

      {/* The details somebody needs to actually find and ring this cell. The drawer used
          to open straight onto the member table, so the station, the contact and the
          location — everything recorded when the cell was formed — were invisible. */}
      <DrawerSection title="Details">
        <dl className="px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-2 text-erp-sm">
          <Detail label="Polling station" value={cell.data?.stationName} />
          <Detail label="Status" value={cell.data ? label(cell.data.statusCode) : null} />
          <Detail label="Contact" value={cell.data?.contactName} />
          <Detail label="Phone" value={cell.data?.contactPhoneMask} mono />
          <Detail label="Members"
            value={cell.data ? `${cell.data.memberCount} of ${cell.data.targetSize}` : null} />
          <Detail label="Formed" value={cell.data ? label(cell.data.formationSourceCode) : null} />
          <Detail label="Meeting point" value={cell.data?.meetingPoint} />
          <Detail label="Location"
            value={cell.data?.latitude != null && cell.data?.longitude != null
              ? `${cell.data.latitude.toFixed(5)}, ${cell.data.longitude.toFixed(5)}`
              : null}
            mono />
          {cell.data?.notes && (
            <div className="col-span-2">
              <dt className="text-erp-xs uppercase tracking-wide text-muted-foreground">Notes</dt>
              <dd className="mt-0.5 whitespace-pre-wrap">{cell.data.notes}</dd>
            </div>
          )}
        </dl>
      </DrawerSection>

      <DrawerSection title="Members"
        right={<span className="text-erp-xs text-muted-foreground tabular-nums">
          {rows.length} of {cell.data?.targetSize ?? 10}
        </span>}>
        {members.loading && rows.length === 0 ? (
          <LoadingBlock label="Loading members…" />
        ) : (
          <ErpTable head={<><Th>Name</Th><Th>Role</Th><Th>Joined</Th><Th /></>}>
            {rows.length === 0 ? (
              <EmptyRow colSpan={4}
                message="Nobody yet. Add the first member with their national ID below." />
            ) : rows.map((m) => (
              <tr key={m.id} className="hover:bg-muted/40">
                <Td>{m.displayName}</Td>
                <Td>{label(m.roleCode)}</Td>
                <Td className="text-muted-foreground">{fmtDateTime(m.joinedAt)}</Td>
                <Td className="text-right whitespace-nowrap">
                  <span className="inline-flex items-center gap-2">
                  {m.roleCode === 'COORDINATOR' ? (
                    <>
                      {/* The number itself needs turnout:read-raw, and the server
                          checks suppression and consent before it parts with one —
                          so this asks at the moment of dialling rather than
                          rendering a link off a field that never held a number. */}
                      <button type="button" className="erp-btn-ghost h-7 text-erp-xs"
                        disabled={busy || !canCallOut}
                        onClick={() => { void call() }}>
                        Call
                      </button>
                      {/* setCoordinator refuses a cell that already has one and says
                          "unassign them first". This is that. */}
                      <button type="button" className="erp-btn-ghost h-7 text-erp-xs"
                        disabled={busy || !canManage}
                        onClick={() => { void run(() => unsetCoordinator(cellId)) }}>
                        Unassign
                      </button>
                    </>
                  ) : (
                    <button type="button" className="erp-btn-ghost h-7 text-erp-xs"
                      disabled={busy || !canManage}
                      onClick={() => { void run(() => setCoordinator(cellId, m.id)) }}>
                      Make coordinator
                    </button>
                  )}
                  {/* Consent is not a one-shot at enrolment: somebody joins at the gate
                      and agrees to be rung two days later. */}
                  <button type="button" className="erp-btn-ghost h-7 text-erp-xs"
                    onClick={() => setOpenConsentFor(
                      openConsentFor === m.id ? null : m.id)}>
                    Consent
                  </button>
                  <button type="button" className="erp-btn-ghost h-7 text-erp-xs text-red-700"
                    disabled={busy || !canEnrol}
                    onClick={() => { void run(() => removeMember(cellId, m.id, 'DECLINED')) }}>
                    Remove
                  </button>
                  </span>
                </Td>
              </tr>
            )).flatMap((row, i) => {
              const m = rows[i]
              if (!m || openConsentFor !== m.id) return [row]
              return [row, (
                <tr key={`${m.id}-consent`}>
                  <td colSpan={4} className="bg-muted/30 px-3 py-2">
                    <MemberConsentPanel voterId={m.voterId} name={m.displayName} />
                  </td>
                </tr>
              )]
            })}
          </ErpTable>
        )}
      </DrawerSection>

      <DrawerSection title="Add a member">
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-end gap-2">
            <label className="text-erp-xs space-y-1 block flex-1">
              <span>National ID</span>
              <input className="erp-input h-9 w-full" value={nationalId} inputMode="numeric"
                onChange={(e) => { setNationalId(e.target.value); setIdentity(null) }}
                onKeyDown={(e) => { if (e.key === 'Enter' && nationalId.trim()) void identify() }}
                placeholder="e.g. 31415926" />
            </label>
            <button type="button" className="erp-btn h-9"
              disabled={busy || !nationalId.trim()} onClick={() => { void identify() }}>
              Find
            </button>
          </div>

          {identity && (
            <Note tone={identity.inRegister ? 'info' : 'bad'}>
              {identity.displayName && (
                <div className="font-medium text-erp-md">{identity.displayName}</div>
              )}
              <div>{identity.sentence}</div>
            </Note>
          )}

          {identity?.inRegister && (
            <>
              <label className="flex items-start gap-2 text-erp-xs">
                <input type="checkbox" className="mt-0.5" checked={consent}
                  onChange={(e) => setConsent(e.target.checked)} />
                <span>
                  They agreed to be listed to a coordinator as one of their group.
                  <span className="block text-muted-foreground">
                    Optional. Ticking it writes a consent record; leaving it writes nothing.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-erp-xs">
                <input type="checkbox" className="mt-0.5" checked={callConsent}
                  onChange={(e) => setCallConsent(e.target.checked)} />
                <span>
                  They agreed to be telephoned.
                  <span className="block text-muted-foreground">
                    Also optional, and askable later — the Consent action on their row
                    records it any time after they join. Without it the coordinator&rsquo;s
                    Call button will refuse.
                  </span>
                </span>
              </label>
              <button type="button" className="erp-btn erp-btn-primary h-9"
                disabled={busy}
                onClick={() => { void run(async () => {
                  await enrolMember(cellId, {
                    voterId: identity.voterId!,
                    consentToMembership: consent,
                  })
                  // A separate append-only row, and deliberately AFTER the enrolment: the
                  // membership is the thing that must succeed, and a consent write that
                  // failed on its own must not lose somebody their place in the cell.
                  if (callConsent) {
                    await recordMemberConsent(identity.voterId!, 'CONTACT_CALL', true)
                  }
                  reset()
                }) }}>
                Add to this cell
              </button>
            </>
          )}

          <Note>
            A voter can be in <strong>one cell per election</strong>. Adding somebody who is
            already in another is refused, and the refusal names the cell they are in.
          </Note>
        </div>
      </DrawerSection>
    </Drawer>
  )
}

/** A labelled cell of the details grid. An absent value reads '—', never blank. */
function Detail({ label: name, value, mono }:
  { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div>
      <dt className="text-erp-xs uppercase tracking-wide text-muted-foreground">{name}</dt>
      <dd className={`mt-0.5 ${mono ? 'font-mono text-erp-xs' : ''}`}>{value || '—'}</dd>
    </div>
  )
}

/**
 * Why the number was withheld, in words that name the fix.
 *
 * CONSENT_NOT_GIVEN and CONSENT_WITHDRAWN refuse the same call and are opposite things to
 * tell somebody: one is "go and ask them", the other is "they said no". They used to share
 * a code, which meant every voter on a register with no consent rows yet was reported as
 * having refused something nobody had put to them.
 */
function whyNotCallable(name: string, reasonCode: string | null): string {
  switch (reasonCode) {
    case 'DO_NOT_CONTACT':
      return `${name} has asked not to be contacted at all.`
    case 'CONSENT_WITHDRAWN':
      return `${name} withdrew consent to be called.`
    case 'CONSENT_NOT_GIVEN':
      return `${name} has not been asked for consent to be called. `
        + 'Record it on their voter record first.'
    case 'NO_ACTIVE_CHANNEL':
      return `The register holds no active phone number for ${name}.`
    case 'VOTER_NOT_ACTIVE':
      return `${name} is not active in the register.`
    default:
      return `${name} cannot be called right now.`
  }
}
